import { Board } from '../core/board';
import { GridPos } from '../core/types';

/**
 * Draws team-colored dashed lines from a piece's origin to its target
 * while it is in transit, so players can track piece movement.
 */
export class MovementLineManager {
  private scene: Phaser.Scene;
  private lines = new Map<string, Phaser.GameObjects.Graphics>();

  private cellSize: number;
  private cellH: number;
  private boardOriginX: number;
  private boardOriginY: number;

  constructor(
    scene: Phaser.Scene,
    cellSize: number,
    cellH: number,
    boardOriginX: number,
    boardOriginY: number,
  ) {
    this.scene = scene;
    this.cellSize = cellSize;
    this.cellH = cellH;
    this.boardOriginX = boardOriginX;
    this.boardOriginY = boardOriginY;
  }

  /** Draw a movement line when a piece starts moving. */
  addLine(pieceId: string, from: GridPos, to: GridPos, team: 'player' | 'enemy'): void {
    // Remove any existing line for this piece
    this.removeLine(pieceId);

    const srcPixel = Board.gridToPixel(from, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);
    const tgtPixel = Board.gridToPixel(to, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);

    const color = team === 'player' ? 0x4a90d9 : 0xd94a4a;
    const alpha = 0.8;
    const lineWidth = 4;
    const dashLen = 10;
    const gapLen = 5;

    const gfx = this.scene.add.graphics();
    gfx.setDepth(12); // above board (10), below pieces/effects

    // Draw dashed line
    this.drawDashedLine(gfx, srcPixel.x, srcPixel.y, tgtPixel.x, tgtPixel.y, color, alpha, lineWidth, dashLen, gapLen);

    // Draw arrowhead at target end
    const angle = Math.atan2(tgtPixel.y - srcPixel.y, tgtPixel.x - srcPixel.x);
    const arrowLen = this.cellSize * 0.18;
    const arrowAngle = Math.PI / 6;
    gfx.lineStyle(lineWidth, color, alpha);
    gfx.beginPath();
    gfx.moveTo(
      tgtPixel.x - arrowLen * Math.cos(angle - arrowAngle),
      tgtPixel.y - arrowLen * Math.sin(angle - arrowAngle),
    );
    gfx.lineTo(tgtPixel.x, tgtPixel.y);
    gfx.lineTo(
      tgtPixel.x - arrowLen * Math.cos(angle + arrowAngle),
      tgtPixel.y - arrowLen * Math.sin(angle + arrowAngle),
    );
    gfx.strokePath();

    this.lines.set(pieceId, gfx);
  }

  /** Fade out and remove the movement line when a piece arrives. */
  removeLine(pieceId: string): void {
    const gfx = this.lines.get(pieceId);
    if (!gfx) return;
    this.lines.delete(pieceId);

    this.scene.tweens.add({
      targets: gfx,
      alpha: 0,
      duration: 200,
      ease: 'Sine.easeOut',
      onComplete: () => gfx.destroy(),
    });
  }

  /** Remove all lines immediately (e.g. on scene shutdown). */
  destroy(): void {
    for (const gfx of this.lines.values()) {
      gfx.destroy();
    }
    this.lines.clear();
  }

  private drawDashedLine(
    gfx: Phaser.GameObjects.Graphics,
    x1: number, y1: number, x2: number, y2: number,
    color: number, alpha: number, lineWidth: number,
    dashLen: number, gapLen: number,
  ): void {
    const totalDx = x2 - x1;
    const totalDy = y2 - y1;
    const dist = Math.sqrt(totalDx * totalDx + totalDy * totalDy);
    if (dist === 0) return;
    const ux = totalDx / dist;
    const uy = totalDy / dist;

    gfx.lineStyle(lineWidth, color, alpha);
    let traveled = 0;
    while (traveled < dist) {
      const segEnd = Math.min(traveled + dashLen, dist);
      gfx.beginPath();
      gfx.moveTo(x1 + ux * traveled, y1 + uy * traveled);
      gfx.lineTo(x1 + ux * segEnd, y1 + uy * segEnd);
      gfx.strokePath();
      traveled = segEnd + gapLen;
    }
  }
}
